import { useMemo } from 'react'
import { CalendarRange } from 'lucide-react'
import { DropdownSelect } from './DropdownSelect'
import { DatePicker } from './DatePicker'

// ─── Types ──────────────────────────────────────────────────────────────────

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_30_days'
  | 'last_month'
  | 'this_year'
  | 'custom'

export interface DateRangeValue {
  /** ISO YYYY-MM-DD */
  from: string
  /** ISO YYYY-MM-DD */
  to: string
  preset: DateRangePreset
}

interface DateRangePresetPickerProps {
  value: DateRangeValue
  onChange: (next: DateRangeValue) => void
  /** Tailwind width class for the preset dropdown. Default: w-44. */
  presetWidthClass?: string
  /** Disable the whole control */
  disabled?: boolean
  size?: 'sm' | 'md'
  /** Restrict / reorder the preset options shown. Defaults to all presets.
   *  `custom` is always appended if omitted so a manual range stays reachable. */
  presets?: DateRangePreset[]
  /** Accessible name for the preset dropdown; the date fields derive theirs from it. */
  ariaLabel?: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const PRESET_LABELS: Record<DateRangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  last_week: 'Past week',
  this_month: 'This month',
  last_30_days: 'Last 30 days',
  last_month: 'Past month',
  this_year: 'This year',
  custom: 'Custom…',
}

const PRESET_OPTIONS: { value: DateRangePreset; label: string }[] = (
  Object.keys(PRESET_LABELS) as DateRangePreset[]
).map((value) => ({ value, label: PRESET_LABELS[value] }))

const toIso = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/** Sunday-start week (matches the date-picker convention used elsewhere in
 *  the app). Returns Sunday 00:00 of the week containing `d`. */
const startOfWeek = (d: Date) => {
  const out = startOfDay(d)
  out.setDate(out.getDate() - out.getDay())
  return out
}

const addDays = (d: Date, n: number) => {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

/** Resolve a preset to a concrete ISO range against today's date. */
export function rangeForPreset(
  preset: DateRangePreset,
  today: Date = new Date(),
): {
  from: string
  to: string
} {
  const t = startOfDay(today)
  switch (preset) {
    case 'today':
      return { from: toIso(t), to: toIso(t) }
    case 'yesterday': {
      const y = addDays(t, -1)
      return { from: toIso(y), to: toIso(y) }
    }
    case 'this_week': {
      const start = startOfWeek(t)
      return { from: toIso(start), to: toIso(t) }
    }
    case 'last_week': {
      const thisWeekStart = startOfWeek(t)
      const lastWeekStart = addDays(thisWeekStart, -7)
      const lastWeekEnd = addDays(thisWeekStart, -1)
      return { from: toIso(lastWeekStart), to: toIso(lastWeekEnd) }
    }
    case 'this_month':
      return { from: toIso(startOfMonth(t)), to: toIso(t) }
    case 'last_30_days':
      // Inclusive of today: today and the 29 days before it (30-day window).
      return { from: toIso(addDays(t, -29)), to: toIso(t) }
    case 'last_month': {
      const lastMonth = new Date(t.getFullYear(), t.getMonth() - 1, 1)
      return { from: toIso(startOfMonth(lastMonth)), to: toIso(endOfMonth(lastMonth)) }
    }
    case 'this_year':
      // Calendar year to date. Deliberately NOT the Nepali fiscal year: this
      // control drives operational reports, and a host reading "this year"
      // over a sales chart means January onward. Fiscal-year windows are
      // chosen explicitly in Finance, where the distinction is the point.
      return { from: toIso(new Date(t.getFullYear(), 0, 1)), to: toIso(t) }
    case 'custom':
    default:
      return { from: toIso(t), to: toIso(t) }
  }
}

/** Convenience initial value for callers - defaults to "Today". */
export function defaultDateRangeValue(preset: DateRangePreset = 'today'): DateRangeValue {
  return { preset, ...rangeForPreset(preset) }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function DateRangePresetPicker({
  value,
  onChange,
  presetWidthClass = 'w-44',
  disabled = false,
  size = 'sm',
  presets,
  ariaLabel = 'Date range',
}: DateRangePresetPickerProps) {
  const presetOptions = useMemo(() => {
    if (!presets || presets.length === 0) return PRESET_OPTIONS
    const wanted: DateRangePreset[] = presets.includes('custom') ? presets : [...presets, 'custom']
    return wanted.map((value) => ({ value, label: PRESET_LABELS[value] }))
  }, [presets])

  const handlePresetChange = (next: string) => {
    const preset = next as DateRangePreset
    if (preset === 'custom') {
      // Preserve whatever from/to the user already had; just flip the mode.
      onChange({ ...value, preset })
      return
    }
    const { from, to } = rangeForPreset(preset)
    onChange({ preset, from, to })
  }

  const handleFromChange = (next: string) => {
    onChange({ ...value, preset: 'custom', from: next })
  }
  const handleToChange = (next: string) => {
    onChange({ ...value, preset: 'custom', to: next })
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className={presetWidthClass}>
        <DropdownSelect
          value={value.preset}
          onChange={handlePresetChange}
          options={presetOptions}
          leftIcon={<CalendarRange size={13} className="text-gray-400" />}
          disabled={disabled}
          size={size}
          ariaLabel={ariaLabel}
        />
      </div>
      {value.preset === 'custom' && (
        // Wraps, and the fields carry the control's own `size` and a fixed
        // width. Without the size they rendered h-9 next to the h-8 preset
        // dropdown, and `size` sets height and font but no width, so a bare
        // wrapper let each field resize with its own text and slide the arrow
        // between them around as dates were picked.
        //
        // The width has to be FIXED rather than a minimum, for that same
        // reason, and wide enough that nothing clips: `w-36` left ~75px for
        // text after the padding, calendar icon, gaps and chevron, and both
        // "Sep 30, 2026" and the "Select date…" placeholder need about that
        // much at text-xs, so they sat on the truncation boundary. `w-40`
        // clears it, and matches the date fields in ConsolidationView.
        <div className="flex flex-wrap items-center gap-2">
          <DatePicker
            value={value.from}
            onChange={(v) => handleFromChange(v)}
            max={value.to || undefined}
            ariaLabel={`${ariaLabel} start`}
            disabled={disabled}
            size={size}
            className="w-40"
          />
          <span className="text-xs text-gray-400">→</span>
          <DatePicker
            value={value.to}
            onChange={(v) => handleToChange(v)}
            min={value.from || undefined}
            ariaLabel={`${ariaLabel} end`}
            disabled={disabled}
            size={size}
            className="w-40"
          />
        </div>
      )}
    </div>
  )
}
